'use memo'

import { useEffect } from 'react'
import { useTypes, type Type } from '@sportninja/common/hooks/useTypes'
import { resolveDefaultPositionId } from '@sportninja/common/features/team/player'
import {
  getSports,
  getAllGenericSportProperties,
} from '@sportninja/common/selectors/types'
import { useSelector } from 'src/hooks/useRedux'

export interface SportOption {
  value: string
  label: string
  isGeneric: boolean
}

export interface SportGroup {
  label: string
  title: string
  options: SportOption[]
}

export interface PositionOption {
  value: string
  label: string
}

export interface ResolvedSport {
  sportId: string
  genericSportId: string
  sportName: string
  defaultPositionId: string
}

interface RawSport {
  id: string | number
  name?: string
  name_full?: string
  sport_id?: string | number
}

interface RawPlayerType {
  id: string | number
  name?: string
  name_full?: string
  is_default?: number | boolean
}

interface FinderSportOptionsResult {
  groups: SportGroup[]
  resolveSport: (optionValue: string) => ResolvedSport | null
  /** Position options ({label,value}) for a sport id, from the /sports/types payload. */
  positionsFor: (sportId: string | null) => PositionOption[]
  loading: boolean
}

/** Pull the raw player_types array for a sport from the /sports/types payload. */
const playerTypesForSport = (
  types: Type[] | null,
  sportId: string
): RawPlayerType[] => {
  if (!sportId || !types) {
    return []
  }
  const sportType = types.find((t) => String(t.sport_id) === String(sportId))
  const entry = sportType?.data?.find(
    (d: { player_types?: RawPlayerType[] }) => d.player_types !== undefined
  )
  return entry?.player_types ?? []
}

/**
 * Builds the grouped (Full Stats / Basic Stats) sport picker options for the
 * finder preference form, plus a resolver and a `positionsFor` helper.
 *
 * Sport options come from the Redux `types` store (`getSports` +
 * `getAllGenericSportProperties`) — the SAME source the web Team Finder form and
 * the create-competition wizard use. Positions come from the fetched
 * `/sports/types` payload via `useTypes` (also like Team Finder), which is
 * reliably populated on demand.
 */
export const useFinderSportOptions = (
  excludeSportIds: string[]
): FinderSportOptionsResult => {
  const { types, fetchTypes, loading } = useTypes()

  useEffect(() => {
    if (!types || types.length === 0) {
      fetchTypes()
    }
  }, [types, fetchTypes])

  const sports = useSelector<Record<string, RawSport>>((state) =>
    getSports(state)
  )
  const genericSports = useSelector<RawSport[]>(
    (state) => getAllGenericSportProperties(state) ?? []
  )

  // `excludeSportIds` holds the id of the sport each existing preference
  // OCCUPIES: a full-stats pref's `sport_id`, or a basic-stats pref's specific
  // `generic_sport_id` (see useFinderPreferenceActions). Each sport — full-stats
  // or basic-stats — is excluded by its OWN id, so used sports drop out but the
  // rest of each group stays selectable.
  const excluded = new Set(excludeSportIds)

  // The parent 'generic' sport id: positions for a basic-stats pick are keyed
  // under it, and it's what a generic preference sends as its `sport_id`.
  const genericParentId = String(
    Object.values(sports ?? {}).find((s) =>
      (s.name ?? '').toLowerCase().includes('generic')
    )?.id ?? ''
  )

  const fullStatsOptions: SportOption[] = Object.values(sports ?? {})
    .filter((s) => !(s.name ?? '').toLowerCase().includes('generic'))
    .filter((s) => !excluded.has(String(s.id)))
    .map((s) => ({
      value: String(s.id),
      label: s.name_full ?? s.name ?? String(s.id),
      isGeneric: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const basicStatsOptions: SportOption[] = (genericSports ?? [])
    .filter((gs) => !excluded.has(String(gs.id)))
    .map((gs) => ({
      value: String(gs.id),
      label: gs.name ?? gs.name_full ?? String(gs.id),
      isGeneric: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const groups: SportGroup[] = [
    { label: 'Full Stats', title: 'fullStats', options: fullStatsOptions },
    { label: 'Basic Stats', title: 'basicStats', options: basicStatsOptions },
  ]

  const positionsFor = (sportId: string | null): PositionOption[] => {
    if (!sportId) {
      return []
    }
    return playerTypesForSport(types, sportId).map((p) => ({
      value: String(p.id),
      label: p.name_full ?? p.name ?? String(p.id),
    }))
  }

  const resolveSport = (optionValue: string): ResolvedSport | null => {
    const generic = basicStatsOptions.find((o) => o.value === optionValue)
    if (generic) {
      // Positions + the BE `sport_id` come from the PARENT 'generic' sport.
      const sportId = genericParentId || optionValue
      return {
        sportId,
        genericSportId: optionValue,
        sportName: generic.label,
        defaultPositionId: resolveDefaultPositionId(
          playerTypesForSport(types, sportId)
        ),
      }
    }
    const full = fullStatsOptions.find((o) => o.value === optionValue)
    if (!full) {
      return null
    }
    return {
      sportId: optionValue,
      genericSportId: '',
      sportName: full.label,
      defaultPositionId: resolveDefaultPositionId(
        playerTypesForSport(types, optionValue)
      ),
    }
  }

  return { groups, resolveSport, positionsFor, loading }
}
